class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.addEventListener('click', (event) => {
      const clearButton = event.target.closest('[data-odyssey-clear-cart]');
      if (!clearButton) return;
      const cartDrawerItems = this.querySelector('cart-drawer-items');
      if (cartDrawerItems?.clearCart) cartDrawerItems.clearCart(event);
    });
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    if (typeof window.odysseyEnsureCartDrawerLineItems === 'function') {
      void window.odysseyEnsureCartDrawerLineItems();
    }
    if (typeof window.odysseySyncCartDrawerCheckoutButton === 'function') {
      window.odysseySyncCartDrawerCheckoutButton();
    }

    document.body.classList.add('overflow-hidden');

    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.classList.add('animate', 'active');
      });
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true }
    );
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
    document.querySelectorAll('.odyssey-pdp-video-features__media video').forEach((video) => {
      if (video.paused) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      }
    });
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    if (!parsedState?.sections) return;

    this.querySelector('.drawer__inner').classList.contains('is-empty') &&
      this.querySelector('.drawer__inner').classList.remove('is-empty');
    this.productId = parsedState.id;
    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      const sectionHtml = parsedState.sections[section.id];
      if (!sectionElement || !sectionHtml) return;
      sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-CartItems',
      },
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '#CartDrawer-Addons',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }

  enableLoading(line) {
    super.enableLoading(line);
    const drawer = document.querySelector('cart-drawer');
    drawer?.classList.add('cart-drawer--syncing');
    drawer?.querySelector('.drawer__footer')?.classList.add('cart-drawer__footer--pending');
  }

  disableLoading(line) {
    super.disableLoading(line);
  }

  finishCartDrawerSync() {
    const sync = window.odysseySyncCartDrawerFooter?.();
    return Promise.resolve(sync).catch(() => {});
  }

  applyCartDrawerSections(parsedState) {
    const sectionHtml = parsedState?.sections?.['cart-drawer'];
    if (!sectionHtml) return;

    if (parsedState.item_count === 0) {
      if (typeof window.odysseyPatchCartDrawerFromSectionHtml === 'function') {
        window.odysseyPatchCartDrawerFromSectionHtml(sectionHtml, { fullEmpty: true });
      }
      return;
    }

    if (typeof window.odysseyPatchCartDrawerFromSectionHtml === 'function') {
      window.odysseyPatchCartDrawerFromSectionHtml(sectionHtml, { fullEmpty: false });
      return;
    }

    this.getSectionsToRender().forEach((section) => {
      if (section.section !== 'cart-drawer') return;
      const elementToReplace =
        document.getElementById(section.id)?.querySelector(section.selector) ||
        document.querySelector(section.selector);
      if (!elementToReplace) return;
      elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
    });
  }

  onCartUpdate() {
    if (window.__odysseyCartDrawerSkipPartialOnCartUpdate) {
      return Promise.resolve();
    }

    const drawer = document.querySelector('cart-drawer');
    drawer?.classList.add('cart-drawer--syncing');
    drawer?.querySelector('.drawer__footer')?.classList.add('cart-drawer__footer--pending');

    return fetch(`${routes.cart_url}?section_id=cart-drawer`, { cache: 'no-store' })
      .then((response) => response.text())
      .then(async (responseText) => {
        if (typeof window.odysseyPatchCartDrawerFromSectionHtml === 'function') {
          window.odysseyPatchCartDrawerFromSectionHtml(responseText, { fullEmpty: false });
        }

        if (typeof window.odysseyEnsureCartDrawerLineItems === 'function') {
          await window.odysseyEnsureCartDrawerLineItems();
        }

        const drawerEl = document.querySelector('cart-drawer');
        const overlay = drawerEl?.querySelector('#CartDrawer-Overlay');
        if (overlay && drawerEl?.close) {
          overlay.replaceWith(overlay.cloneNode(true));
          drawerEl.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawerEl.close());
        }

        return window.odysseySyncCartDrawerFooter?.();
      })
      .then(() => {
        window.requestAnimationFrame(() => {
          if (typeof window.odysseyCartDrawerAddonsScan === 'function') {
            window.odysseyCartDrawerAddonsScan({ soft: true });
          }
          if (typeof window.odysseyEnrichCartAccessoryColors === 'function') {
            void window.odysseyEnrichCartAccessoryColors();
          }
        });
      })
      .catch((error) => {
        console.error(error);
        drawer?.classList.remove('cart-drawer--syncing');
        drawer?.querySelector('.drawer__footer')?.classList.remove('cart-drawer__footer--pending');
      });
  }

  updateQuantity(line, quantity, event, name, variantId) {
    this.enableLoading(line);

    const sections = this.getSectionsToRender();
    const body = JSON.stringify({
      line,
      quantity,
      sections: sections.map((section) => section.section),
      sections_url:
        typeof window.odysseyCartDrawerSectionsUrl === 'function'
          ? window.odysseyCartDrawerSectionsUrl()
          : window.routes?.cart_url || '/cart',
    });

    const eventTarget = event.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';

    return fetch(`${routes.cart_change_url}`, { ...fetchConfig(), body })
      .then((response) => response.text())
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (typeof window.odysseyPaintCartDrawerTotal === 'function' && parsedState.total_price != null) {
          window.odysseyPaintCartDrawerTotal(parsedState.total_price);
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');
          cartFooter?.classList.toggle('is-empty', parsedState.item_count === 0);
          cartDrawerWrapper?.classList.toggle('is-empty', parsedState.item_count === 0);

          this.applyCartDrawerSections(parsedState);

          sections.forEach((section) => {
            if (section.section === 'cart-drawer') return;
            const elementToReplace =
              document.getElementById(section.id)?.querySelector(section.selector) ||
              document.getElementById(section.id);
            if (!elementToReplace || !parsedState.sections?.[section.section]) return;
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });

          if (typeof window.odysseyPaintCartDrawerTotal === 'function' && parsedState.total_price != null) {
            window.odysseyPaintCartDrawerTotal(parsedState.total_price);
          }

          const updatedValue = parsedState.items[line - 1] ? parsedState.items[line - 1].quantity : undefined;
          let message = '';
          if (
            items.length === parsedState.items.length &&
            updatedValue !== parseInt(quantityElement.value, 10)
          ) {
            message =
              typeof updatedValue === 'undefined'
                ? window.cartStrings.error
                : window.cartStrings.quantityError.replace('[quantity]', updatedValue);
          }

          this.updateLiveRegions(line, message);

          const lineItem =
            document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          if (lineItem?.querySelector(`[name="${name}"]`)) {
            if (cartDrawerWrapper) {
              trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`));
            } else {
              lineItem.querySelector(`[name="${name}"]`).focus();
            }
          } else if (parsedState.item_count === 0 && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        CartPerformance.measureFromEvent(`${eventTarget}:user-action`, event);

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-items',
          cartData: parsedState,
          variantId,
        });

        window.requestAnimationFrame(() => {
          if (typeof window.odysseyCartDrawerAddonsScan === 'function') {
            window.odysseyCartDrawerAddonsScan({ soft: true });
          }
          if (typeof window.odysseyEnrichCartAccessoryColors === 'function') {
            void window.odysseyEnrichCartAccessoryColors();
          }
        });
      })
      .catch(() => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        this.finishCartDrawerSync().finally(() => {
          this.disableLoading(line);
        });
      });
  }

  applyClearedCartSections(cartDrawerWrapper, sections, sectionResponse) {
    sections.forEach((section) => {
      const sectionHtml = sectionResponse[section.section];
      if (!sectionHtml) return;

      if (section.id === 'CartDrawer') {
        const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
        const sourceInner = parsed.querySelector('.drawer__inner');
        const targetInner = document.querySelector('cart-drawer .drawer__inner');
        if (sourceInner && targetInner) {
          targetInner.innerHTML = sourceInner.innerHTML;
        }
        cartDrawerWrapper
          ?.querySelector('#CartDrawer-Overlay')
          ?.addEventListener('click', cartDrawerWrapper.close.bind(cartDrawerWrapper));
      } else {
        const elementToReplace =
          document.getElementById(section.id)?.querySelector(section.selector) ||
          document.getElementById(section.id);
        if (elementToReplace) {
          elementToReplace.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        }
      }
    });
  }

  clearCart(event) {
    event.preventDefault();
    const trigger = event.currentTarget;
    if (trigger.getAttribute('aria-disabled') === 'true') return;
    trigger.setAttribute('aria-disabled', 'true');

    const mainCartItems = document.getElementById('CartDrawer-CartItems');
    mainCartItems?.classList.add('cart__items--disabled');

    const sections = this.getSectionsToRender();
    const sectionQuery = sections.map((section) => section.section).join(',');
    const sectionsUrl =
      typeof window.odysseyCartDrawerSectionsUrl === 'function'
        ? window.odysseyCartDrawerSectionsUrl()
        : window.routes?.cart_url || '/cart';

    fetch(`${routes.cart_url}/clear.js`, {
      method: 'POST',
      ...fetchConfig(),
      body: JSON.stringify({
        sections: sectionQuery,
        sections_url: sectionsUrl,
      }),
    })
      .then((response) => {
        if (!response.ok) throw new Error('Clear cart failed');
        return response.json();
      })
      .then((parsedState) => {
        const cartDrawerWrapper = document.querySelector('cart-drawer');
        const isEmpty = parsedState.item_count === 0;
        cartDrawerWrapper?.classList.toggle('is-empty', isEmpty);
        this.classList.toggle('is-empty', isEmpty);

        if (isEmpty && cartDrawerWrapper) {
          cartDrawerWrapper.close();
        }

        const sectionResponse = parsedState.sections || {};
        this.applyClearedCartSections(cartDrawerWrapper, sections, sectionResponse);

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState });
      })
      .catch(() => {
        const errors = document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
      })
      .finally(() => {
        mainCartItems?.classList.remove('cart__items--disabled');
        trigger.removeAttribute('aria-disabled');
      });
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
